import React, { useEffect, useMemo } from 'react';
import { Theme, useMediaQuery, useTheme } from '@material-ui/core';
import uPlot from 'uplot';
import UplotReact from 'uplot-react';
import 'uplot/dist/uPlot.min.css';
import { isEmpty } from 'lodash';
import { NoData, formatDateForApi, useFormatDate } from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import {
  ActuationsBaseModel,
  BoundaryAndActuationsBaseModel,
  GraphActuationsOptionsFn,
  GraphActuationsType,
  GraphBoundariesModel,
  GraphBoundariesOptionsFn,
  GraphBoundariesType,
  MainGraphDataModel,
  MainGraphFiltersStateModel,
  PMZoneSiteModel,
  SiteActuationsModel,
  FlowUnitsOptionsFn,
  PressureUnitsOptionsFn,
} from '@shared/models';
import { ShowStateModel } from '../MainGraph';
import {
  axesDynamicScalesPlugin,
  axesWidthPlugin,
  boundsPlugin,
  BoundsPluginParams,
  CustomChannelNamesFn,
  drawFrameOnGraph,
  mainGraphChannelLabelAndColor,
  midnightLinePlugin,
  ShowBoundLineType,
  tooltipPlugin,
} from './Graph.utils';
import { GraphControls } from '../GraphControls';
import { isNil } from 'lodash';
import { ACTUATIONS_GRAPH_SCALE_NAME } from '@features/GraphsView/Actuations/Actuations';

const LOADING_DELAY = 2000;
export const GRAPH_HEIGHT_FACTOR = 160;

interface GraphProps {
  bandsData: GraphBoundariesModel<number[]>;
  size: {
    height: number;
    width: number;
  };
  sitesData: PMZoneSiteModel[];
  isPrinting: boolean;
  graphData: MainGraphDataModel[];
  showState: ShowStateModel;
  setShowState: React.Dispatch<React.SetStateAction<ShowStateModel>>;
  actuationsData: SiteActuationsModel[];
  solenoidActuationThreshold: number;
  isGraphLoading: boolean;
  plotRef: React.MutableRefObject<uPlot>;
  setPlotRef?: (plotRef: uPlot) => void;
  graphRendered: boolean;
  setGraphRendered: React.Dispatch<React.SetStateAction<boolean>>;
  currFiltersState: MainGraphFiltersStateModel;
  /**
   * `dataTestId` - name of a Graph (preferably `<graph_name>`)
   * @example
   * ```tsx
   * <Graph {...props} dataTestId="test-graph" />
   */
  dataTestId: string;
}

export const Graph: React.FC<GraphProps> = ({
  bandsData,
  size,
  sitesData,
  isPrinting,
  graphData,
  showState,
  setShowState,
  actuationsData,
  solenoidActuationThreshold,
  isGraphLoading,
  plotRef,
  setPlotRef,
  graphRendered,
  setGraphRendered,
  currFiltersState,
  dataTestId,
}) => {
  const theme = useTheme<Theme>();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const extraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const { formatDate } = useFormatDate();

  useEffect(() => {
    isGraphLoading && setGraphRendered(false);
    const timer = setTimeout(() => {
      !isGraphLoading && setGraphRendered(true);
    }, LOADING_DELAY);
    return () => clearTimeout(timer);
  }, [isGraphLoading, setGraphRendered]);

  const dataContainsFlowSeries = useMemo(
    () => graphData?.some((el) => el.channelData.some((el) => el.channelSummary.channelType === 'Flow')),
    [graphData]
  );

  //- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const combinedDates = useMemo(() => {
    const channelsDates =
      graphData?.flatMap((gd) =>
        gd?.channelData?.flatMap((chD) => chD?.deviceData?.flatMap((dD) => formatDateForApi(dD.date)))
      ) ?? [];

    const actuationsDates = actuationsData?.[0]?.actuationData?.map((el) => formatDateForApi(el.date)) ?? [];

    const combinedDates = [...channelsDates, ...actuationsDates].sort((a, b) => Date.parse(a) - Date.parse(b));

    const uniqDates = Array.from(new Set(combinedDates)) ?? [];
    const sortedUniqDates = uniqDates?.sort((a, b) => Date.parse(a) - Date.parse(b)) ?? [];

    return sortedUniqDates;
  }, [graphData, actuationsData]);

  const channelsDictionary: Record<string, number>[] = useMemo(() => {
    const dictionary = graphData?.flatMap(({ channelData }) =>
      channelData?.map(
        ({ deviceData }) =>
          Object.fromEntries(deviceData?.map((dd) => [formatDateForApi(dd.date), dd.value]) ?? []) ?? {}
      )
    );

    return dictionary;
  }, [graphData]);

  // additional axes

  const graphAdditionalAxes = useMemo(() => {
    const axesData =
      graphData?.flatMap((d) =>
        d.channelData
          ?.filter(
            (el) =>
              el.channelSummary.channelType !== 'Flow' &&
              el.channelSummary.channelType !== 'Pressure' &&
              !isNil(el.channelSummary.channelType)
          )
          ?.map((chD) => chD.channelSummary.channelType)
      ) ?? [];

    const axes =
      axesData?.map((label, i) => ({
        scale: label,
        label: label,
        stroke: '#000000',
        show: showState.axes,
        grid: { show: false },
        side: i % 2 === 0 ? 3 : 1,
        labelFont: 'bold 13px Ubuntu Sans',
        font: '12px Ubuntu Sans',
      })) ?? [];

    return axes;
  }, [graphData, showState.axes]);

  //channels

  const channelsGraphDataAndSeries = useMemo(() => {
    const channels: number[][] = combinedDates?.length
      ? channelsDictionary?.map(() => Array.from(Array(combinedDates?.length)))
      : [];

    channels?.forEach((channel, i) => {
      combinedDates?.forEach((el, j) => {
        channel[j] = channelsDictionary[i]?.[el] ?? null;
      });
    });

    let colorIterator = 0;

    const series =
      graphData?.flatMap(({ siteSummary, channelData }, i) =>
        channelData.map(({ channelSummary }, j) => {
          const { channelNo, channelType } = channelSummary;
          const siteInfo = sitesData?.find((site) => site.id === siteSummary.siteId);
          const isFlowChannel = channelType === 'Flow';
          const isPressureChannel = channelType === 'Pressure';
          const isPrv = siteInfo?.siteType === 'PRV';

          const { label, color } = mainGraphChannelLabelAndColor(
            siteInfo?.address ?? '',
            channelSummary,
            colorIterator,
            isPrv
          );

          colorIterator = colorIterator + (isPrv && channelNo <= CustomChannelNamesFn().length ? 0 : 1);

          const tooltipLabel = `${channelType?.replaceAll(' ', '_')}${(isFlowChannel
            ? `_[${FlowUnitsOptionsFn()[currFiltersState.flowUnit]}]`
            : isPressureChannel
              ? `_[${PressureUnitsOptionsFn()[currFiltersState.pressureUnit]}]`
              : ''
          )?.replaceAll(' ', '_')}`;

          return {
            label,
            scale: channelType,
            class: tooltipLabel,
            stroke: color,
            show: Boolean(showState.channels?.[i]?.[j]),
            spanGaps: true,
            points: { show: false },
          } as uPlot.Series;
        })
      ) ?? [];

    return {
      values: showState.channels && channels?.length > 0 ? channels : [],
      series: showState.channels && channels?.length > 0 ? series : [],
    };
  }, [
    sitesData,
    graphData,
    combinedDates,
    showState.channels,
    channelsDictionary,
    currFiltersState.flowUnit,
    currFiltersState.pressureUnit,
  ]);

  //actuations
  const actuationsDictionary = useMemo(() => {
    const data = actuationsData?.[0]?.actuationData;

    const actuations: Record<string, Record<GraphActuationsType, number>> = Object.fromEntries(
      data?.map((el) => [
        formatDateForApi(el.date),
        {
          [GraphActuationsType.Positive]: el.positiveActuations,
          [GraphActuationsType.Negative]: el.negativeActuations,
          [GraphActuationsType.Total]: el.positiveActuations + el.negativeActuations,
          [GraphActuationsType.Recommended]: solenoidActuationThreshold ?? null,
        },
      ]) ?? []
    );

    return actuations;
  }, [actuationsData, solenoidActuationThreshold]);

  // actuations
  useEffect(() => {
    if (!isGraphLoading && !showState.actuations && actuationsDictionary && !isEmpty(actuationsDictionary)) {
      const data = Object.values(actuationsDictionary);

      const showPositive = data.some((value) => Number.isFinite(value[GraphActuationsType.Positive]));
      const showNegative = data.some((value) => Number.isFinite(value[GraphActuationsType.Negative]));
      const showTotal = showPositive && showNegative;
      const showRecommended =
        (showPositive || showNegative) && data.some((value) => Number.isFinite(value[GraphActuationsType.Recommended]));

      const GraphActuationsOptions = GraphActuationsOptionsFn();

      const actuations: Partial<Record<GraphActuationsType, ActuationsBaseModel>> = {
        ...(showPositive
          ? {
              [GraphActuationsType.Positive]: GraphActuationsOptions[GraphActuationsType.Positive],
            }
          : {}),
        ...(showNegative
          ? {
              [GraphActuationsType.Negative]: GraphActuationsOptions[GraphActuationsType.Negative],
            }
          : {}),
        ...(showTotal
          ? {
              [GraphActuationsType.Total]: GraphActuationsOptions[GraphActuationsType.Total],
            }
          : {}),
        ...(showRecommended
          ? {
              [GraphActuationsType.Recommended]: GraphActuationsOptions[GraphActuationsType.Recommended],
            }
          : {}),
      };

      setShowState((p) => ({ ...p, actuations }));
    }
  }, [isGraphLoading, showState.actuations, setShowState, actuationsDictionary]);

  const actuationsGraphDataAndSeries = useMemo(() => {
    if (!showState.actuations) return { series: [], values: [] };

    const values = [] as number[][];
    const series = [] as uPlot.Series[];

    const allHidden = Object.values(showState.actuations)?.every((el) => !el?.state);

    Object.entries(showState.actuations).forEach(([key, { label, scale, state, color }]) => {
      const type = key as GraphActuationsType;

      const data: number[] = combinedDates?.length ? Array.from(Array(combinedDates?.length)) : [];

      combinedDates?.forEach((el, i) => {
        data[i] = actuationsDictionary?.[el]?.[type] ?? null;
      });

      values.push(data);
      series.push(
        type !== GraphActuationsType.Recommended
          ? ({
              label,
              scale,
              stroke: color,
              width: state ? 1.4 : allHidden ? 0 : 0.001,
              spanGaps: true,
              points: { show: false },
            } as uPlot.Series)
          : ({
              label,
              scale,
              stroke: color,
              width: state ? 2 : allHidden ? 0 : 0.001,
              spanGaps: true,
              dash: [8],
              points: { show: false },
            } as uPlot.Series)
      );
    });

    return { values, series };
  }, [combinedDates, showState.actuations, actuationsDictionary]);

  // bounds
  useEffect(() => {
    if (
      !isGraphLoading &&
      !showState.bands &&
      channelsGraphDataAndSeries?.series &&
      !isEmpty(channelsGraphDataAndSeries.series)
    ) {
      const bands: GraphBoundariesModel<BoundaryAndActuationsBaseModel> = Object.fromEntries(
        Object.entries(bandsData)
          ?.map(([key, values]) => {
            const [max, min] = values as number[];

            if (Number.isFinite(max) || Number.isFinite(min)) {
              const type = key as GraphBoundariesType;
              return [type, { ...GraphBoundariesOptionsFn()[type] }];
            } else {
              return null;
            }
          })
          .filter(Boolean)
      );

      setShowState((p) => ({ ...p, bands }));
    }
  }, [bandsData, isGraphLoading, showState.bands, channelsGraphDataAndSeries?.series, setShowState]);

  const graphBoundaries = useMemo(() => {
    if (!showState.bands) return { data: [], series: [], pluginData: [] };

    const boundaries = Object.entries(showState.bands).map(([key, bands]) => {
      const type = key as GraphBoundariesType;
      const { label, scale, state } = bands as BoundaryAndActuationsBaseModel;

      const [upperVal, lowerVal] =
        bandsData[type]?.[0] >= bandsData[type]?.[1] ? bandsData[type] : bandsData[type].reverse();

      const correspondingSeries = channelsGraphDataAndSeries.series.filter((s) => s.scale === scale);

      const valuesForSelectedSeries = correspondingSeries.flatMap(
        (el) => channelsGraphDataAndSeries.values?.[channelsGraphDataAndSeries.series.indexOf(el)]
      );

      const maxVal = Math.max(...valuesForSelectedSeries) * 1.05;
      const minVal = Math.min(...valuesForSelectedSeries, 0) * 1.05;

      const max = Number.isFinite(Math.ceil(maxVal)) ? Math.ceil(maxVal) : 1;
      const min = Number.isFinite(Math.floor(minVal)) ? Math.floor(minVal) : 0;

      const showUpperBoundLine: ShowBoundLineType = Number.isFinite(upperVal)
        ? upperVal > min
          ? upperVal <= max
            ? ShowBoundLineType.DEFAULT
            : ShowBoundLineType.TOP
          : ShowBoundLineType.BOTTOM
        : ShowBoundLineType.NONE;

      const showLowerBoundLine: ShowBoundLineType = Number.isFinite(lowerVal)
        ? lowerVal < max
          ? lowerVal >= min
            ? ShowBoundLineType.DEFAULT
            : ShowBoundLineType.BOTTOM
          : ShowBoundLineType.TOP
        : ShowBoundLineType.NONE;

      const series = [] as uPlot.Series[];

      if (showUpperBoundLine === ShowBoundLineType.DEFAULT) {
        series.push({
          label,
          scale,
          stroke: 'black',
          show: state,
          class: 'boundary',
          spanGaps: true,
          dash: [16],
          width: 1.2,
          points: {
            show: false,
          },
        });
      }
      if (showLowerBoundLine === ShowBoundLineType.DEFAULT) {
        series.push({
          label,
          scale,
          stroke: 'black',
          show: state,
          class: 'boundary',
          spanGaps: true,
          dash: [16],
          width: 1.2,
          points: {
            show: false,
          },
        });
      }

      const show = [showUpperBoundLine, showLowerBoundLine];

      const values = [Number.isFinite(upperVal) ? upperVal : null, Number.isFinite(lowerVal) ? lowerVal : null];

      return {
        data: [
          ...(showUpperBoundLine === ShowBoundLineType.DEFAULT ? [combinedDates?.map(() => upperVal) ?? []] : []),
          ...(showLowerBoundLine === ShowBoundLineType.DEFAULT ? [combinedDates?.map(() => lowerVal) ?? []] : []),
        ],
        series,
        state: state,
        pluginData: {
          label,
          scale,
          show,
          values,
        } as BoundsPluginParams,
      };
    });

    return {
      data: boundaries?.flatMap((b) => b.data) ?? [],
      series: boundaries?.flatMap((b) => b.series) ?? [],
      pluginData: boundaries?.filter((b) => b.state)?.map((b) => b.pluginData) ?? null,
    };
  }, [bandsData, combinedDates, showState.bands, channelsGraphDataAndSeries.series, channelsGraphDataAndSeries.values]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  useEffect(() => {
    setShowState((p) => ({
      ...p,
      empty:
        !graphData ||
        isEmpty(graphData) ||
        !graphData.some((el) => el.channelData && !isEmpty(el.channelData)) ||
        !combinedDates ||
        isEmpty(combinedDates),
    }));
  }, [graphData, combinedDates, setShowState]);

  return (
    <>
      {Boolean(size.height && graphRendered) ? (
        showState.empty ? (
          <div
            style={{
              height: smallScreen ? 360 : Math.max(232, size.height - GRAPH_HEIGHT_FACTOR),
            }}
          >
            <NoData dataTestId={`${dataTestId}-no-data`} />
          </div>
        ) : (
          <div className="relative rounded-lg pb-2 mr-2 z-50" style={{ background: '#eeeeee' }}>
            {extraSmallScreen && showState.panArrows && (
              <div className="absolute top-1 right-1 z-50">
                <GraphControls plotRef={plotRef} dataTestId={`${dataTestId}-controls`} />
              </div>
            )}
            <UplotReact
              onCreate={setPlotRef}
              options={{
                legend: {
                  show: false,
                  live: false,
                } as uPlot.Legend,
                width: size.width,
                height: Math.round(
                  isPrinting ? size.height : smallScreen ? 360 : Math.max(232, size.height - GRAPH_HEIGHT_FACTOR)
                ),
                padding: [20, showState.axes ? 2 : 32, 4, showState.axes ? 0 : 32],
                cursor: {
                  drag: {
                    x: true,
                    y: true,
                    dist: 12, // min distance to use zoom (px)
                    uni: 24, // distance to zoom in both directions
                  },
                } as uPlot.Cursor,

                axes: [
                  {
                    space: 64,
                    font: '11px Ubuntu Sans',
                    values: (_, splits) => {
                      const parsedDates = splits.map((d) => d * 1000);
                      const datesToDisplay = parsedDates.map((d, i, arr) => {
                        const date = new Date(d);

                        const currDate = { day: date.getDate(), month: date.getMonth() };
                        const prev = new Date(arr[i - 1]);
                        const prevDate = {
                          day: i !== 0 ? prev.getDate() : -1,
                          month: i !== 0 ? prev.getMonth() : -1,
                        };

                        const showFullDate = currDate.day !== prevDate.day || currDate.month !== prevDate.month;

                        // if 1st date to display or current is next day -> display day-month-year
                        return (showFullDate ? `${formatDate(date, 'P')}\n` : '') + formatDate(date, 'HH:mm');
                      });

                      return datesToDisplay;
                    },
                  },
                  {
                    scale: 'Pressure',
                    label: `${t({
                      message: 'Pressure',
                      id: '[PegasusTable.Pressure]',
                    })} [${PressureUnitsOptionsFn()[currFiltersState.pressureUnit]}]`,
                    stroke: '#000000',
                    grid: { show: !dataContainsFlowSeries },
                    show: showState.axes,
                    labelFont: 'bold 13px Ubuntu Sans',
                    font: '12px Ubuntu Sans',
                  },
                  {
                    scale: 'Flow',
                    label: `${t({ message: 'Flow', id: '[PegasusTable.Flow]' })} [${
                      FlowUnitsOptionsFn()[currFiltersState.flowUnit]
                    }]`,
                    side: 1,
                    stroke: '#000000',
                    show: showState.axes,
                    labelFont: 'bold 13px Ubuntu Sans',
                    font: '12px Ubuntu Sans',
                  },
                  ...(actuationsGraphDataAndSeries.series.length > 0 && actuationsGraphDataAndSeries.values.length > 0
                    ? [
                        {
                          scale: ACTUATIONS_GRAPH_SCALE_NAME,
                          label: t`Actuations`,
                          stroke: '#000000',
                          side: 1,
                          grid: { show: false },
                          show: showState.axes,
                          labelFont: 'bold 13px Ubuntu Sans',
                          font: '12px Ubuntu Sans',
                        },
                      ]
                    : []),
                  ...graphAdditionalAxes,
                ],

                series: [
                  {},
                  ...actuationsGraphDataAndSeries.series,
                  ...channelsGraphDataAndSeries.series,
                  ...graphBoundaries.series,
                ],

                plugins: [
                  drawFrameOnGraph(),
                  tooltipPlugin(smallScreen, formatDate),
                  midnightLinePlugin(),
                  axesWidthPlugin(),
                  boundsPlugin(extraSmallScreen, graphBoundaries.pluginData),
                  axesDynamicScalesPlugin(showState.scales, (scales) => setShowState((p) => ({ ...p, scales }))),
                ],
              }}
              data={[
                combinedDates.map((el) => Date.parse(el) / 1000),
                ...actuationsGraphDataAndSeries.values,
                ...channelsGraphDataAndSeries.values,
                ...graphBoundaries.data,
              ]}
            />
          </div>
        )
      ) : (
        <div style={{ height: 232 }} />
      )}
    </>
  );
};
